// apps/web/src/services/pushNotifications.ts

/**
 * Push Notifications Service
 * Handles browser push notifications and service worker registration
 */

interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: any;
  tag?: string;
  requireInteraction?: boolean;
  actions?: NotificationAction[];
}

class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private publicVapidKey: string = '';
  private isRegistering: boolean = false;

  constructor() {
    // Get VAPID key from environment
    this.publicVapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
  }

  /**
   * Check if push notifications are supported
   */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    const permission = await Notification.requestPermission();
    console.log('📢 Notification permission:', permission);
    return permission;
  }

  /**
   * Register service worker
   */
  async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!this.isSupported()) {
      throw new Error('Service workers are not supported');
    }

    // Prevent multiple simultaneous registrations
    if (this.isRegistering) {
      console.log('⏳ Service worker registration already in progress...');
      await this.waitForRegistration();
      if (this.registration) return this.registration;
    }

    // Check if already registered
    const existingRegistration = await navigator.serviceWorker.getRegistration();
    if (existingRegistration) {
      console.log('✅ Service Worker already registered:', existingRegistration.scope);
      this.registration = existingRegistration;
      return existingRegistration;
    }

    this.isRegistering = true;

    try {
      // Register the service worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('✅ Service Worker registered:', this.registration.scope);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('✅ Service Worker ready');

      return this.registration;
    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
      throw error;
    } finally {
      this.isRegistering = false;
    }
  }

  /**
   * Wait for ongoing registration to complete
   */
  private async waitForRegistration(): Promise<void> {
    const maxWait = 5000; // 5 seconds
    const startTime = Date.now();

    while (this.isRegistering && Date.now() - startTime < maxWait) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Ensure service worker is registered
   */
  private async ensureRegistration(): Promise<ServiceWorkerRegistration> {
    if (this.registration) {
      return this.registration;
    }

    const existingRegistration = await navigator.serviceWorker.getRegistration();
    if (existingRegistration) {
      this.registration = existingRegistration;
      return existingRegistration;
    }

    return await this.registerServiceWorker();
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(): Promise<PushSubscription> {
    // Ensure service worker is registered
    await this.ensureRegistration();

    if (!this.registration) {
      throw new Error('Service worker not registered');
    }

    try {
      // Check if already subscribed
      let subscription = await this.registration.pushManager.getSubscription();

      if (subscription) {
        console.log('📱 Already subscribed to push notifications');
        return subscription;
      }

      // Subscribe to push notifications
      subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.publicVapidKey),
      });

      console.log('✅ Subscribed to push notifications');
      return subscription;
    } catch (error) {
      console.error('❌ Push subscription failed:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    try {
      // Get current registration
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (!registration) {
        console.log('⚠️ No service worker registration found');
        return false;
      }

      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        const success = await subscription.unsubscribe();
        console.log('🔕 Unsubscribed from push notifications');
        return success;
      }

      return false;
    } catch (error) {
      console.error('❌ Unsubscribe failed:', error);
      throw error;
    }
  }

  /**
   * Get current subscription
   */
  async getSubscription(): Promise<PushSubscription | null> {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (!registration) {
        return null;
      }

      return await registration.pushManager.getSubscription();
    } catch (error) {
      console.error('❌ Failed to get subscription:', error);
      return null;
    }
  }

  /**
   * Show local notification (without push server)
   */
  async showLocalNotification(payload: PushNotificationPayload): Promise<void> {
    if (!this.isSupported()) {
      console.warn('Notifications not supported');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    // Ensure service worker is registered
    await this.ensureRegistration();

    if (this.registration) {
      await this.registration.showNotification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/favicons/transparent-favicon.png',
        badge: payload.badge || '/favicons/transparent-favicon.png',
        data: payload.data,
        tag: payload.tag,
        requireInteraction: payload.requireInteraction || false,
        actions: payload.actions,
      });
    }
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();

// Export types
export type { PushNotificationPayload };